import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { CaretSortIcon, PlusIcon } from "@radix-ui/react-icons";
import { Button } from "@/lib/shadcn/ui/button";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/lib/shadcn/ui/dropdown-menu";
import { cn } from "@/lib/shadcn/utils";
import { Input } from "@/lib/shadcn/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/lib/shadcn/ui/tooltip";
import { useAddTagMutation, useGetAllTagsQuery } from "@/store/booking-management-api";

interface TagMultiSelectProps {
    onChange: (tagIds: number[]) => void;
    placeholder?: string;
    selectedTagIds?: number[];
    variant?: "default" | "destructive" | "outline" | "secondary" | "volt" | "ghost" | "link";
    className?: string;
    disabled?: boolean;
}

export const TagMultiSelect: React.FC<TagMultiSelectProps> = ({
    onChange,
    placeholder = "Select tags...",
    selectedTagIds = [],
    variant = "outline",
    className,
    disabled = false,
}) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [open, setOpen] = useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const { data: tags = [], isLoading } = useGetAllTagsQuery();
    const [addTag, { isLoading: isCreatingTag }] = useAddTagMutation();

    React.useEffect(() => {
        if (!open) {
            setSearchQuery("");
        }
    }, [open]);

    const tagOptions = useMemo(
        () =>
            tags.map((tag) => ({
                value: tag.id.toString(),
                label: tag.name,
                tag,
            })),
        [tags],
    );

    const selectedTags = useMemo(() => tags.filter((tag) => selectedTagIds.includes(tag.id)), [tags, selectedTagIds]);

    const filteredOptions = useMemo(
        () => tagOptions.filter((option) => option.label.toLowerCase().includes(searchQuery.toLowerCase())),
        [tagOptions, searchQuery],
    );

    const exactMatch = useMemo(
        () => searchQuery.trim() && tags.some((tag) => tag.name.toLowerCase() === searchQuery.toLowerCase().trim()),
        [tags, searchQuery],
    );

    const getDisplayText = useCallback(() => {
        if (selectedTags.length === 0) return placeholder;

        const displayCount = 2;
        if (selectedTags.length <= displayCount) {
            return selectedTags.map((tag) => tag.name).join(", ");
        }

        return `${selectedTags
            .slice(0, displayCount)
            .map((tag) => tag.name)
            .join(", ")} +${selectedTags.length - displayCount} more`;
    }, [selectedTags, placeholder]);

    const getTooltipText = useCallback(() => {
        if (selectedTags.length === 0) return placeholder;
        return selectedTags.map((tag) => tag.name).join(", ");
    }, [selectedTags, placeholder]);

    const handleCheckedChange = useCallback(
        (tagId: string, checked: boolean) => {
            const id = parseInt(tagId);
            const newSelected = checked ? [...selectedTagIds, id] : selectedTagIds.filter((existingId) => existingId !== id);
            onChange(newSelected);
        },
        [selectedTagIds, onChange],
    );

    const handleCreateTag = useCallback(async () => {
        const trimmedQuery = searchQuery.trim();
        if (!trimmedQuery || exactMatch || isCreatingTag) return;

        try {
            const result = await addTag({ name: trimmedQuery }).unwrap();
            // Add the newly created tag to selection
            onChange([...selectedTagIds, result.id]);
            setSearchQuery("");
        } catch (error) {
            console.error("Failed to create tag:", error);
        }
    }, [searchQuery, exactMatch, isCreatingTag, addTag, selectedTagIds, onChange]);

    const handleInputClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        inputRef.current?.focus();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        setSearchQuery(e.target.value);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        e.stopPropagation();
        if (e.key === "Enter" && searchQuery.trim() && !exactMatch && !isCreatingTag) {
            e.preventDefault();
            handleCreateTag();
        }
    };

    const showCreateOption = searchQuery.trim() && !exactMatch && !isCreatingTag;

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <DropdownMenuTrigger asChild>
                            <Button variant={variant} className={cn("w-full justify-between overflow-hidden", className)} disabled={disabled || isLoading}>
                                <span className="truncate w-[90%]">{getDisplayText()}</span>
                                <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                        </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p className="max-w-xs wrap-break-word">{getTooltipText()}</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
            <DropdownMenuContent
                className={cn("w-(--radix-dropdown-menu-trigger-width)")}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-2">
                    <Input
                        ref={inputRef}
                        placeholder="Search or create tag..."
                        aria-label="Search or create tag"
                        value={searchQuery}
                        onChange={handleInputChange}
                        onClick={handleInputClick}
                        onKeyDown={handleKeyDown}
                        className="h-8"
                        disabled={disabled || isLoading}
                    />
                </div>
                <div className="overflow-y-auto max-h-[200px]">
                    {showCreateOption && (
                        <DropdownMenuItem onClick={handleCreateTag} className="text-primary cursor-pointer" disabled={isCreatingTag}>
                            <PlusIcon className="mr-2 h-4 w-4" />
                            Create "{searchQuery.trim()}"
                        </DropdownMenuItem>
                    )}
                    {filteredOptions.length === 0 && !showCreateOption ? (
                        <div className="text-sm text-muted-foreground p-2">{isLoading ? "Loading tags..." : "No tags found"}</div>
                    ) : (
                        filteredOptions.map((option) => (
                            <DropdownMenuCheckboxItem
                                key={option.value}
                                checked={selectedTagIds.includes(parseInt(option.value))}
                                onCheckedChange={(checked) => handleCheckedChange(option.value, checked)}
                                onSelect={(e) => e.preventDefault()}
                            >
                                {option.label}
                            </DropdownMenuCheckboxItem>
                        ))
                    )}
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
